<template>
  <div class="pa-10">
    <title-page icon="mdi-calendar-check">
      {{ $t('revenue-cutoff.title') }}
    </title-page>

    <v-breadcrumbs
        :items="[
            {text: $t('reports.reports'), to: {name: 'reports'}, exact: true},
            {text: $t('revenue-cutoff.title')},
        ]"
        divider=">"
    ></v-breadcrumbs>

    <v-card outlined class="mb-6">
      <v-card-text>
        <div class="d-flex flex-wrap align-center" style="gap: 16px">
          <v-select
              v-model="cutoffMonth"
              :items="cutoffMonthItems"
              :label="$t('revenue-cutoff.cutoff-month')"
              prepend-inner-icon="mdi-calendar-check"
              hide-details
              outlined
              dense
              style="max-width: 220px"
          ></v-select>
          <v-select
              v-model="exercise"
              :items="exerciseItems"
              :label="$t('revenue-cutoff.exercise')"
              prepend-inner-icon="mdi-calendar-range"
              hide-details
              outlined
              dense
              style="max-width: 160px"
          ></v-select>

          <v-spacer></v-spacer>

          <v-checkbox
              v-model="includeReimbursements"
              :label="$t('revenue-cutoff.include-reimbursements')"
              hide-details
              dense
              class="mt-0 mr-2"
          ></v-checkbox>

          <div class="d-flex align-center">
            <v-btn-toggle v-model="mode" mandatory dense color="primary">
              <v-btn small value="spread">{{ $t('revenue-cutoff.mode-spread') }}</v-btn>
              <v-btn small value="billing">{{ $t('revenue-cutoff.mode-billing') }}</v-btn>
            </v-btn-toggle>
            <v-tooltip bottom max-width="320">
              <template #activator="{on, attrs}">
                <v-icon small class="ml-2 grey--text" v-on="on" v-bind="attrs">mdi-information-outline</v-icon>
              </template>
              <span>{{ $t('revenue-cutoff.mode-hint') }}</span>
            </v-tooltip>
          </div>

          <!-- Le fichier reprend les réglages affichés : il doit montrer ce que l'écran montre. -->
          <v-btn outlined small color="primary" :loading="exporting" @click="exportWorkbook">
            <v-icon left small>mdi-microsoft-excel</v-icon>
            {{ $t('revenue-cutoff.export') }}
          </v-btn>
        </div>

        <div class="caption grey--text text--darken-1 mt-3">
          <v-icon x-small class="mr-1">mdi-camera-outline</v-icon>{{ $t('revenue-cutoff.hint') }}
        </div>
      </v-card-text>
    </v-card>

    <v-card outlined v-if="matrix">
      <v-card-title class="subtitle-1 font-weight-bold">
        {{ $t('revenue-cutoff.matrix-title', {exercise: matrix.exercise, month: cutoffMonthLabel}) }}
        <v-chip
            v-if="matrix.provisionedSource === 'reconstructed'"
            x-small
            color="orange lighten-4"
            class="ml-3 orange--text text--darken-3"
        >
          <v-icon x-small left>mdi-alert-outline</v-icon>
          {{ $t('revenue-cutoff.reconstructed') }}
        </v-chip>
      </v-card-title>
      <v-card-text>
        <div class="table-scroll">
          <table class="cutoff-table">
            <thead>
              <tr>
                <th class="text-left label-col">{{ $t('revenue-cutoff.recognition') }}</th>
                <th v-for="m in matrix.months" :key="m" class="text-right">{{ monthLabel(m) }}</th>
                <th class="text-right total-col">{{ $t('revenue-cutoff.total') }}</th>
              </tr>
            </thead>
            <tbody>
              <!-- Provisionné -->
              <tr class="group-row">
                <td class="label-col">{{ $t('revenue-cutoff.provisioned') }}</td>
                <td v-for="m in matrix.months" :key="m" class="text-right">
                  <a v-if="hasValue(matrix.provisioned.byMonth, m)" class="drill" @click="openBreakdown('provisioned', m)">{{ money(sideMonth('provisioned', m)) }}</a>
                  <span v-else>—</span>
                </td>
                <td class="text-right total-col">{{ money(sideTotal('provisioned')) }}</td>
              </tr>
              <tr class="sub-row">
                <td class="label-col pl-8">{{ $t('revenue-cutoff.licences') }}</td>
                <td v-for="m in matrix.months" :key="m" class="text-right">{{ cell(matrix.provisioned.licence, m) }}</td>
                <td class="text-right total-col">{{ money(sum(matrix.provisioned.licence)) }}</td>
              </tr>
              <tr class="sub-row">
                <td class="label-col pl-8">{{ $t('revenue-cutoff.usage') }}</td>
                <td v-for="m in matrix.months" :key="m" class="text-right">{{ cell(matrix.provisioned.usage, m) }}</td>
                <td class="text-right total-col">{{ money(sum(matrix.provisioned.usage)) }}</td>
              </tr>
              <tr class="sub-row reimb-row" v-if="includeReimbursements">
                <td class="label-col pl-8">{{ $t('revenue-cutoff.reimbursements') }}</td>
                <td v-for="m in matrix.months" :key="m" class="text-right">{{ cell(matrix.provisioned.reimbursement, m) }}</td>
                <td class="text-right total-col">{{ money(sum(matrix.provisioned.reimbursement)) }}</td>
              </tr>

              <!-- Facturé -->
              <tr class="group-row">
                <td class="label-col">{{ $t('revenue-cutoff.billed') }}</td>
                <td v-for="m in matrix.months" :key="m" class="text-right">
                  <a v-if="hasValue(matrix.billed.byMonth, m)" class="drill" @click="openBreakdown('billed', m)">{{ money(sideMonth('billed', m)) }}</a>
                  <span v-else>—</span>
                </td>
                <td class="text-right total-col">{{ money(sideTotal('billed')) }}</td>
              </tr>
              <tr class="sub-row">
                <td class="label-col pl-8">{{ $t('revenue-cutoff.licences') }}</td>
                <td v-for="m in matrix.months" :key="m" class="text-right">{{ cell(matrix.billed.licence, m) }}</td>
                <td class="text-right total-col">{{ money(sum(matrix.billed.licence)) }}</td>
              </tr>
              <tr class="sub-row">
                <td class="label-col pl-8">{{ $t('revenue-cutoff.usage') }}</td>
                <td v-for="m in matrix.months" :key="m" class="text-right">{{ cell(matrix.billed.usage, m) }}</td>
                <td class="text-right total-col">{{ money(sum(matrix.billed.usage)) }}</td>
              </tr>
              <tr class="sub-row reimb-row" v-if="includeReimbursements">
                <td class="label-col pl-8">{{ $t('revenue-cutoff.reimbursements') }}</td>
                <td v-for="m in matrix.months" :key="m" class="text-right">{{ cell(matrix.billed.reimbursement, m) }}</td>
                <td class="text-right total-col">{{ money(sum(matrix.billed.reimbursement)) }}</td>
              </tr>

              <!-- Total reconnu -->
              <tr class="total-row">
                <td class="label-col">{{ $t('revenue-cutoff.recognized-total') }}</td>
                <td v-for="m in matrix.months" :key="m" class="text-right">{{ money(recognized(m)) }}</td>
                <td class="text-right total-col">{{ money(sideTotal('provisioned') + sideTotal('billed')) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="caption grey--text text--darken-1 mt-3">
          <v-icon x-small>mdi-cursor-default-click-outline</v-icon>
          {{ $t('revenue-cutoff.drill-hint') }}
        </div>
      </v-card-text>
    </v-card>

    <!-- Carry-over (past) + deferred revenue (future) -->
    <v-row class="mt-2" v-if="matrix">
      <!-- Report des exercices antérieurs (passé) -->
      <v-col cols="12" md="6">
        <v-card outlined class="fill-height">
          <v-card-title class="subtitle-1 font-weight-bold">
            {{ $t('revenue-cutoff.carry-title') }}
          </v-card-title>
          <v-card-text>
            <table class="cutoff-table carry-table">
              <tbody>
                <tr v-for="y in carryOverYears" :key="y" class="sub-row">
                  <td class="label-col">{{ $t('revenue-cutoff.carry-year', {year: y}) }}</td>
                  <td class="text-right">{{ money(matrix.carryOver.byYear[y]) }}</td>
                </tr>
                <tr v-if="!carryOverYears.length">
                  <td class="label-col grey--text">{{ $t('revenue-cutoff.carry-none') }}</td>
                  <td class="text-right">—</td>
                </tr>
                <tr class="group-row">
                  <td class="label-col">{{ $t('revenue-cutoff.carry-total') }}</td>
                  <td class="text-right">{{ money(matrix.carryOver.total) }}</td>
                </tr>
                <tr class="total-row">
                  <td class="label-col">{{ $t('revenue-cutoff.total-open-provisions') }}</td>
                  <td class="text-right">{{ money(matrix.carryOver.total + matrix.provisioned.total) }}</td>
                </tr>
              </tbody>
            </table>
            <div class="caption grey--text text--darken-1 mt-3">
              <v-icon x-small class="mr-1">mdi-information-outline</v-icon>{{ $t('revenue-cutoff.carry-hint') }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Produits constatés d'avance (futur) -->
      <v-col cols="12" md="6">
        <v-card outlined class="fill-height">
          <v-card-title class="subtitle-1 font-weight-bold">
            {{ $t('revenue-cutoff.deferred-title') }}
          </v-card-title>
          <v-card-text>
            <table class="cutoff-table carry-table">
              <tbody>
                <tr v-for="y in deferredYears" :key="y" class="sub-row">
                  <td class="label-col">{{ $t('revenue-cutoff.deferred-year', {year: y}) }}</td>
                  <td class="text-right">{{ money(matrix.deferredRevenue.byYear[y]) }}</td>
                </tr>
                <tr v-if="!deferredYears.length">
                  <td class="label-col grey--text">{{ $t('revenue-cutoff.deferred-none') }}</td>
                  <td class="text-right">—</td>
                </tr>
                <tr class="total-row">
                  <td class="label-col">{{ $t('revenue-cutoff.deferred-total') }}</td>
                  <td class="text-right">{{ money(matrix.deferredRevenue.total) }}</td>
                </tr>
              </tbody>
            </table>
            <div class="caption grey--text text--darken-1 mt-3">
              <v-icon x-small class="mr-1">mdi-information-outline</v-icon>{{ $t('revenue-cutoff.deferred-hint') }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Drill-down -->
    <v-dialog v-model="breakdownDialog" max-width="900" scrollable>
      <v-card v-if="breakdownContext">
        <v-card-title class="subtitle-1">
          <v-icon left>{{ breakdownContext.side === 'provisioned' ? 'mdi-timer-sand' : 'mdi-file-document-check-outline' }}</v-icon>
          {{ $t('revenue-cutoff.' + breakdownContext.side) }} — {{ monthLongLabel(breakdownContext.month) }}
          <v-spacer></v-spacer>
          <v-btn icon @click="breakdownDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4" style="max-height: 70vh;">
          <div v-if="breakdownLoading" class="text-center py-8">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </div>
          <template v-else-if="breakdown">
            <div class="d-flex align-center mb-3">
              <span class="subtitle-2">{{ $tc('revenue-cutoff.client-count', breakdown.clients.length, {count: breakdown.clients.length}) }}</span>
              <v-spacer></v-spacer>
              <span class="subtitle-2 font-weight-bold">{{ money(breakdown.total) }}</span>
            </div>
            <v-expansion-panels multiple accordion flat>
              <v-expansion-panel v-for="(client, i) in breakdown.clients" :key="i">
                <v-expansion-panel-header>
                  <div class="d-flex align-center" style="width: 100%">
                    <span class="font-weight-medium">{{ client.recipientName || $t('revenue-cutoff.unnamed') }}</span>
                    <v-chip x-small class="ml-2" outlined>{{ client.recipientType }}</v-chip>
                    <v-spacer></v-spacer>
                    <span class="font-weight-bold mr-2">{{ money(client.total) }}</span>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div v-for="(doc, j) in client.documents" :key="j" class="doc-block">
                    <div class="d-flex align-center">
                      <v-icon x-small class="mr-1">{{ doc.documentType === 'invoice' ? 'mdi-receipt-text-outline' : 'mdi-file-clock-outline' }}</v-icon>
                      <a class="font-weight-medium piece-link" @click="openDoc(doc)">
                        {{ doc.reference || '—' }}<v-icon x-small class="ml-1">mdi-magnify</v-icon>
                      </a>
                      <v-chip x-small class="ml-2" :color="doc.contentType === 'usage' ? 'blue-grey lighten-4' : 'teal lighten-4'">
                        {{ $t('revenue-cutoff.' + (doc.contentType === 'usage' ? 'usage' : 'subscriptions')) }}
                      </v-chip>
                      <v-spacer></v-spacer>
                      <span class="font-weight-bold">{{ money(doc.monthAmount) }}</span>
                    </div>
                    <div class="caption grey--text text--darken-1 ml-5">
                      {{ doc.coverageStart }} → {{ doc.coverageEnd }}
                      <template v-if="doc.coverageMonths > 1">
                        · {{ $t('revenue-cutoff.spread-over', {n: doc.coverageMonths}) }}
                        · {{ $t('revenue-cutoff.full-amount', {amount: money(doc.fullAmount)}) }}
                      </template>
                    </div>
                    <div v-for="(s, k) in doc.subscriptions" :key="k" class="caption ml-8 subscription-line">
                      <v-icon x-small class="mr-1">mdi-tag-outline</v-icon>
                      {{ s.name }}
                      <span class="float-right">{{ money(s.amount) }}</span>
                    </div>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
            <div v-if="!breakdown.clients.length" class="text-center grey--text py-6">
              {{ $t('revenue-cutoff.no-detail') }}
            </div>
          </template>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- The clicked piece opens the standard proforma / invoice modal — the proforma in its FROZEN state
         (from the snapshot) so it matches what was provisioned, the invoice live since invoices are immutable. -->
    <proforma-invoice-show-modal ref="proformaModal" />
    <invoice-show-modal ref="invoiceModal" />
  </div>
</template>

<script>
import BillingControlRepository from "@repository/BillingControlRepository"
import ProformaInvoiceShowModal from "@pages/ProformaInvoice/modals/ProformaInvoiceShowModal.vue"
import InvoiceShowModal from "@pages/Invoice/modals/InvoiceShowModal.vue"
import dayjs from "dayjs"

export default {
  components: {
    ProformaInvoiceShowModal,
    InvoiceShowModal,
  },

  data() {
    const lastClosed = dayjs().subtract(1, 'month')
    return {
      cutoffMonth: lastClosed.format('YYYY-MM'),
      exercise: lastClosed.year(),
      mode: 'spread',
      includeReimbursements: true,
      exporting: false,
      matrix: null,
      breakdownDialog: false,
      breakdownLoading: false,
      breakdown: null,
      breakdownContext: null,
    }
  },

  mounted() {
    this.loadData()
  },

  watch: {
    cutoffMonth() { this.loadData() },
    exercise() { this.loadData() },
    mode() { this.loadData() },
  },

  computed: {
    cutoffMonthItems() {
      // From the last closed month back to the first proforma period (start of activity), reported by the
      // API as cutoffMin — instead of a fixed window.
      const items = []
      let cursor = dayjs().subtract(1, 'month').startOf('month')
      const min = (this.matrix && this.matrix.cutoffMin)
        ? dayjs(`${this.matrix.cutoffMin}-01`)
        : cursor.subtract(23, 'month')
      while (cursor.isAfter(min) || cursor.isSame(min, 'month')) {
        items.push({
          value: cursor.format('YYYY-MM'),
          text: cursor.format('MMMM YYYY'),
        })
        cursor = cursor.subtract(1, 'month')
      }
      return items
    },

    exerciseItems() {
      // Activity started in 2023 — expose every exercise since then (carry-over / report), and extend into
      // the future up to the last year that still holds recognition data (deferred revenue on a multi-year
      // subscription), reported by the API as exerciseMax.
      const firstExercise = 2023
      const current = dayjs().year()
      const last = Math.max(current, (this.matrix && this.matrix.exerciseMax) || current)
      const years = []
      for (let y = last; y >= firstExercise; y--) {
        years.push(y)
      }
      return years
    },

    cutoffMonthLabel() {
      return dayjs(`${this.cutoffMonth}-01`).format('MMMM YYYY')
    },

    // Prior-exercise years present in the carry-over, most recent first.
    carryOverYears() {
      const byYear = this.matrix && this.matrix.carryOver && this.matrix.carryOver.byYear
      if (!byYear) {
        return []
      }
      return Object.keys(byYear).map(Number).sort((a, b) => b - a)
    },

    // Future-exercise years present in the deferred revenue, earliest first.
    deferredYears() {
      const byYear = this.matrix && this.matrix.deferredRevenue && this.matrix.deferredRevenue.byYear
      if (!byYear) {
        return []
      }
      return Object.keys(byYear).map(Number).sort((a, b) => a - b)
    },
  },

  methods: {
    loadData() {
      this.showLoading(true)
      BillingControlRepository.revenueCutoff({
        cutoffMonth: this.cutoffMonth,
        exercise: this.exercise,
        mode: this.mode,
      }).then(res => {
        this.matrix = res
        this.showLoading(false)
      }).catch(err => {
        this.notifyError(err)
        this.showLoading(false)
      })
    },

    // Le classeur sort avec les mêmes paramètres que l'écran ; l'interrupteur « remboursements » n'y est
    // pas repris, ils y sont une colonne, que le lecteur garde ou retire comme il veut.
    exportWorkbook() {
      this.exporting = true
      BillingControlRepository.revenueCutoffExport({
        cutoffMonth: this.cutoffMonth,
        exercise: this.exercise,
        mode: this.mode,
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.exporting = false
      })
    },

    openBreakdown(side, month) {
      this.breakdownContext = {side, month}
      this.breakdown = null
      this.breakdownDialog = true
      this.breakdownLoading = true
      BillingControlRepository.revenueCutoffBreakdown({
        cutoffMonth: this.cutoffMonth,
        exercise: this.exercise,
        mode: this.mode,
        month,
        side,
      }).then(res => {
        this.breakdown = res
        this.breakdownLoading = false
      }).catch(err => {
        this.notifyError(err)
        this.breakdownLoading = false
      })
    },

    monthLabel(month) {
      return dayjs(`${month}-01`).format('MMM')
    },

    monthLongLabel(month) {
      return dayjs(`${month}-01`).format('MMMM YYYY')
    },

    hasValue(series, month) {
      return !!(series && series[month])
    },

    cell(series, month) {
      const value = series && series[month]
      return value ? this.money(value) : '—'
    },

    money(value) {
      return `${this.numberFormat(value || 0)} €`
    },

    sum(series) {
      return Object.values(series || {}).reduce((acc, v) => acc + v, 0)
    },

    // Per-month value for a side, dropping the reimbursements when the toggle is off (byMonth includes them).
    sideMonth(side, month) {
      const s = this.matrix[side]
      const base = (s.byMonth && s.byMonth[month]) || 0
      if (this.includeReimbursements) {
        return base
      }
      return base - ((s.reimbursement && s.reimbursement[month]) || 0)
    },

    sideTotal(side) {
      const s = this.matrix[side]
      return this.includeReimbursements ? s.total : s.total - this.sum(s.reimbursement)
    },

    recognized(month) {
      return this.sideMonth('provisioned', month) + this.sideMonth('billed', month)
    },

    // Open the clicked piece in the standard modal. Invoices are immutable, so the live invoice modal is a
    // faithful as-of view. Proformas move, so when the cell comes from a snapshot we open the proforma
    // FROZEN (snapshotMonth) to match what was provisioned; for a reconstructed month there is no snapshot,
    // so we fall back to the live proforma.
    openDoc(doc) {
      if (!doc.documentId) {
        return
      }
      if (doc.documentType === 'invoice') {
        this.$refs.invoiceModal.open(doc.documentId)
        return
      }
      const frozen = this.breakdown && this.breakdown.source === 'snapshot'
      this.$refs.proformaModal.open(doc.documentId, frozen ? {snapshotMonth: this.cutoffMonth} : {})
    },
  },
}
</script>

<style scoped>
.table-scroll {
  overflow-x: auto;
}

.cutoff-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
  white-space: nowrap;
}

.cutoff-table th,
.cutoff-table td {
  padding: 8px 12px;
  border-bottom: 1px solid #eceff1;
}

.cutoff-table thead th {
  font-weight: 600;
  color: #607d8b;
  border-bottom: 2px solid #cfd8dc;
}

.label-col {
  text-align: left;
  min-width: 200px;
}

.total-col {
  font-weight: 600;
  border-left: 1px solid #cfd8dc;
}

.group-row td {
  font-weight: 600;
  background: #f5f7f8;
}

.sub-row td {
  color: #546e7a;
}

.reimb-row td {
  color: #90a4ae;
  font-style: italic;
}

.total-row td {
  font-weight: 700;
  border-top: 2px solid #cfd8dc;
  background: #eef4f3;
}

.drill {
  cursor: pointer;
  text-decoration: underline dotted;
}

.doc-block {
  padding: 6px 0;
  border-bottom: 1px solid #f0f0f0;
}

.subscription-line {
  color: #78909c;
  padding: 1px 0;
}

.carry-table {
  white-space: normal;
}

.carry-table .label-col {
  min-width: 0;
}

.piece-link {
  color: #00695c;
  text-decoration: none;
}

.piece-link:hover {
  text-decoration: underline;
}
</style>
